import { Logo, Brand } from "@/components/Logo";
import { ContactSupport } from "@/components/ContactSupport";
import { openInstall, isStandalone } from "@/components/InstallPrompt";
import NotificationBell from "@/components/NotificationBell";
import { useNavigate } from "react-router-dom";
import { MessageSquarePlus, Trash2, MessageCircle, Zap, Lock, Crown, CreditCard, Info, ShieldCheck, Download, Newspaper, Images } from "lucide-react";

export default function Sidebar({ view, conversations, activeId, onSelect, onNew, onDelete,
  isPro, isOwner, proExpiresAt, usage, onUpgrade, onManageBilling }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col h-full bg-[#0a0a0f]">
      <div className="p-5 border-b border-[#2a3040] flex items-start justify-between">
        <div>
          <Logo size="text-2xl" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#9ca3af] mt-1">The Unfair Advantage</p>
        </div>
        <NotificationBell align="start" />
      </div>

      <div className="p-3">
        <button data-testid="new-chat-button" onClick={onNew}
          className="w-full flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-4 py-2.5 hover:bg-[#FF7A33] transition-colors">
          <MessageSquarePlus size={18} /> New chat
        </button>
        <button data-testid="sidebar-articles" onClick={() => navigate("/articles")}
          className="w-full flex items-center gap-2.5 px-3 py-2 mt-1 rounded-lg text-sm text-[#9ca3af] hover:text-white hover:bg-[#15151d] transition-colors">
          <Newspaper size={16} className="text-[#FF5A00]" /> Articles
        </button>
        {isPro && (
          <button data-testid="sidebar-photos" onClick={() => navigate("/photos")}
            className="w-full flex items-center gap-2.5 px-3 py-2 mt-1 rounded-lg text-sm text-[#9ca3af] hover:text-white hover:bg-[#15151d] transition-colors">
            <Images size={16} className="text-[#FF5A00]" /> Photos
          </button>
        )}
      </div>

      {/* Usage / Pro */}
      {!isPro && (
        <div className="mx-3 mb-2 p-3 rounded-lg bg-[#15151d] border border-[#2a3040]" data-testid="usage-meter">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#9ca3af]">Daily chats</span>
            <span className="text-xs font-bold text-white">{usage ? `${usage.used}/${usage.limit}` : "–/10"}</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#0a0a0f] overflow-hidden mb-3">
            <div className="h-full bg-[#FF5A00] transition-all"
              style={{ width: usage ? `${Math.min(100, (usage.used / usage.limit) * 100)}%` : "0%" }} />
          </div>
          <button data-testid="go-pro-button" onClick={onUpgrade}
            className="w-full flex items-center justify-center gap-1.5 text-xs font-bold bg-[#FF5A00]/15 text-[#FF5A00] border border-[#FF5A00]/30 rounded-lg px-3 py-2 hover:bg-[#FF5A00]/25 transition-colors">
            <Zap size={13} /> Upgrade to Pro
          </button>
        </div>
      )}
      {isPro && (
        <div className="mx-3 mb-2 p-3 rounded-lg bg-[#FF5A00]/10 border border-[#FF5A00]/30" data-testid="pro-panel">
          <div className="flex items-center gap-1.5 text-xs font-bold text-[#FF5A00] mb-2">
            <Crown size={13} /> <Brand /> Pro active
          </div>
          {proExpiresAt && (
            <p className="text-[10px] text-[#9ca3af] mb-2" data-testid="pro-expiry-note">
              Expires {new Date(proExpiresAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
            </p>
          )}
          <button data-testid="pro-features-button" onClick={() => navigate("/pro")}
            className="w-full flex items-center justify-center gap-1.5 text-xs font-medium bg-[#15151d] text-white border border-[#2a3040] rounded-lg px-3 py-2 mb-2 hover:border-[#FF5A00] transition-colors">
            <Crown size={13} className="text-[#FF5A00]" /> Your Pro features
          </button>
          <button data-testid="manage-billing-button" onClick={onManageBilling}
            className="w-full flex items-center justify-center gap-1.5 text-xs font-medium bg-[#15151d] text-white border border-[#2a3040] rounded-lg px-3 py-2 hover:border-[#FF5A00] transition-colors">
            <CreditCard size={13} /> Manage billing
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3 min-h-0" />

      <div className="p-3 border-t border-[#2a3040] space-y-0.5">
        {isOwner && (
          <button data-testid="sidebar-owner" onClick={() => navigate("/owner")}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold text-[#FF5A00] hover:bg-[#FF5A00]/10 transition-colors">
            <Crown size={16} /> Owner Console
          </button>
        )}
        <button data-testid="sidebar-about" onClick={() => navigate("/about")}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-[#9ca3af] hover:text-white hover:bg-[#15151d] transition-colors">
          <Info size={16} className="text-[#FF5A00]" /> About <Brand />
        </button>
        <ContactSupport />
        {!isStandalone() && (
          <button data-testid="sidebar-install" onClick={openInstall}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-[#9ca3af] hover:text-white hover:bg-[#15151d] transition-colors">
            <Download size={16} className="text-[#FF5A00]" /> Add to Home Screen
          </button>
        )}
        <button data-testid="sidebar-privacy" onClick={() => navigate("/privacy")}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-[#9ca3af] hover:text-white hover:bg-[#15151d] transition-colors">
          <ShieldCheck size={16} className="text-[#FF5A00]" /> Privacy Policy
        </button>
      </div>
    </div>
  );
}
